import React, { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { TrajectoryData } from '../types';

interface TrajectoryPathProps {
  trajectory: TrajectoryData;
  color: string;
  opacity?: number;
  animated?: boolean;
  animationTime?: number;
  lineWidth?: number;
  dashed?: boolean;
}

export const TrajectoryPath: React.FC<TrajectoryPathProps> = ({
  trajectory,
  color,
  opacity = 1,
  animated = false,
  animationTime = 0,
  lineWidth = 5,
  dashed = false
}) => {
  const lineRef = useRef<THREE.Line>(null);

  // Convert trajectory points to Vector3 array
  const points = useMemo(() => {
    return trajectory.points.map(point => 
      new THREE.Vector3(point.position.x, point.position.y, point.position.z)
    );
  }, [trajectory]);

  // Create geometry
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    return geo;
  }, [points]);

  // Animated geometry for progressive reveal
  const animatedGeometry = useMemo(() => {
    if (!animated || !animationTime) return geometry;
    
    // Calculate how many points to show based on animation time
    const totalFlightTime = trajectory.flightTime * 1000; // Convert to ms
    const progress = Math.min(1, Math.max(0, (animationTime - 2000) / totalFlightTime)); // Start after pre-impact
    const pointsToShow = Math.floor(points.length * progress);
    
    if (pointsToShow <= 1) return new THREE.BufferGeometry();
    
    const animatedPoints = points.slice(0, pointsToShow);
    return new THREE.BufferGeometry().setFromPoints(animatedPoints);
  }, [geometry, animated, animationTime, trajectory.flightTime, points]);

  // Update geometry when animation changes
  useEffect(() => {
    if (lineRef.current) {
      lineRef.current.geometry = animated ? animatedGeometry : geometry;
    }
  }, [geometry, animatedGeometry, animated]);

  // Update dash geometry if dashed
  useEffect(() => {
    if (dashed && lineRef.current) {
      lineRef.current.computeLineDistances();
    }
  }, [dashed, geometry, animatedGeometry]);

  // Get points to display based on animation
  const displayPoints = useMemo(() => {
    if (!animated || animationTime === 0) return points;
    
    const progress = Math.min(1, Math.max(0, (animationTime - 2000) / (trajectory.flightTime * 1000)));
    const pointsToShow = Math.floor(points.length * progress);
    
    return points.slice(0, Math.max(2, pointsToShow));
  }, [points, animated, animationTime, trajectory.flightTime]);

  // Create a mesh line for better visibility
  const lineGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(displayPoints.length * 3);
    
    displayPoints.forEach((point, i) => {
      positions[i * 3] = point.x;
      positions[i * 3 + 1] = point.y;
      positions[i * 3 + 2] = point.z;
    });
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geometry;
  }, [displayPoints]);

  return (
    <group>
      {/* Main trajectory as a series of spheres for visibility */}
      {displayPoints.map((point, index) => {
        if (index % 3 !== 0) return null; // Show every 3rd point
        return (
          <mesh key={index} position={[point.x, point.y, point.z]}>
            <sphereGeometry args={[lineWidth * 0.02, 8, 8]} />
            <meshBasicMaterial color={color} transparent opacity={opacity} />
          </mesh>
        );
      })}
      
      {/* Connect with lines */}
      <primitive
        object={new THREE.Line(lineGeometry, new THREE.LineBasicMaterial({ color, transparent: true, opacity, linewidth: lineWidth }))}
        ref={lineRef}
      />
      
      {/* Peak height marker */}
      {!animated && (
        <mesh position={[0, trajectory.maxHeight, trajectory.points[Math.floor(trajectory.points.length / 2)]?.position.z || 0]}>
          <sphereGeometry args={[0.1]} />
          <meshBasicMaterial color={color} transparent opacity={0.7} />
        </mesh>
      )}
      
      {/* Landing point marker */}
      {(!animated || animationTime > 4000) && trajectory.points.length > 0 && (
        <group position={[
          trajectory.points[trajectory.points.length - 1].position.x,
          0.1,
          trajectory.points[trajectory.points.length - 1].position.z
        ]}>
          <mesh>
            <cylinderGeometry args={[0.5, 0.5, 0.1]} />
            <meshBasicMaterial color={color} transparent opacity={0.5} />
          </mesh>
          {/* Distance text would go here in a real implementation */}
        </group>
      )}
      
      {/* Velocity vectors at key points */}
      {!animated && trajectory.points.length > 10 && (
        <>
          {trajectory.points.filter((_, i) => i % 20 === 0).map((point, index) => (
            <group key={index} position={[point.position.x, point.position.y, point.position.z]}>
              <arrowHelper
                args={[
                  point.velocity.clone().normalize(),
                  new THREE.Vector3(0, 0, 0),
                  point.velocity.length() * 0.1,
                  color,
                  0.2,
                  0.1
                ]}
              />
            </group>
          ))}
        </>
      )}
      
      {/* Spin indicators */}
      {!animated && trajectory.points.length > 5 && (
        <>
          {trajectory.points.filter((_, i) => i % 30 === 0).map((point, index) => (
            <mesh
              key={`spin-${index}`}
              position={[point.position.x, point.position.y + 0.3, point.position.z]}
            >
              <torusGeometry args={[0.1, 0.02, 8, 16]} />
              <meshBasicMaterial color={color} transparent opacity={0.4} />
            </mesh>
          ))}
        </>
      )}
    </group>
  );
};